// Vue 2 兼容版本
// 移除了 TypeScript 类型定义，使用 JavaScript 注释说明参数类型

import { FieldType } from './enum'
import { capitalizeFirstLetter, getName, getSelectDataByFilterOptions } from './useFast'

/**
 * 获取请求参数，1、过滤空值 2、通用商品需要特殊处理 _section 和 _single 结尾的参数
 * @param {Object} filter 过滤器
 * @param {boolean} isGeneralGoods 是否为通用商品
 * @returns {Object} 请求参数
 */
export function formatParams(filter, isGeneralGoods = false) {
  const result = {}
  const groupedParams = {}

  // 遍历过滤器对象的所有键值对
  for (const [key, value] of Object.entries(filter)) {
    // 跳过空值
    if (['', null, undefined].includes(value))
      continue

    // 通用商品需要特殊处理 _section 和 _single 结尾的参数
    // _section: min_price_section、max_price_section -> {section: {price: {min: 1, max: 2}}}
    // _single: single1_single -> {single: {single1: 1}}
    if (isGeneralGoods) {
      // 处理 _section 结尾的字段：min_price_section, max_Aaa_section
      if (key.endsWith('_section')) {
        const fieldNameWithPrefix = key.substring(0, key.indexOf('_section'))
        let minMaxKey = ''
        let fieldName = ''

        // 提取 min_/max_ 前缀和字段名
        if (fieldNameWithPrefix.startsWith('min_')) {
          minMaxKey = 'min'
          fieldName = fieldNameWithPrefix.substring(4) // 去掉 'min_'
        }
        else if (fieldNameWithPrefix.startsWith('max_')) {
          minMaxKey = 'max'
          fieldName = fieldNameWithPrefix.substring(4) // 去掉 'max_'
        }

        if (minMaxKey && fieldName) {
          if (!groupedParams.section) {
            groupedParams.section = {}
          }
          if (!groupedParams.section[fieldName]) {
            groupedParams.section[fieldName] = {}
          }
          groupedParams.section[fieldName][minMaxKey] = value
          continue
        }
      }

      // 处理 _single 结尾的字段
      if (key.endsWith('_single')) {
        const fieldName = key.substring(0, key.indexOf('_single'))
        if (!groupedParams.single) {
          groupedParams.single = {}
        }
        groupedParams.single[fieldName] = value
        continue
      }
    }
    // 非通用商品：min_xxx -> minXxx, max_xxx -> maxXxx
    else {
      if (key.startsWith('min_') || key.startsWith('max_')) {
        const prefix = key.startsWith('min_') ? 'min' : 'max'
        const fieldName = key.substring(4) // 去掉 'min_' 或 'max_'
        const camelCaseKey = prefix + capitalizeFirstLetter(fieldName)
        result[camelCaseKey] = value
        continue
      }
    }

    // 其他参数正常处理
    result[key] = value
  }

  // 将分组参数添加到结果中
  Object.assign(result, groupedParams)

  return result
}

/**
 * 查询参数转换为过滤器格式
 * @param {Object} opt 游戏商品配置属性
 * @param {Object} query 查询参数
 * @returns {Object} 过滤器对象
 */
export function query2Filter(opt, query) {
	if ([FieldType.SINGLE, FieldType.LABEL, FieldType.CHECKBOX].includes(opt.fieldType)) {
		if (opt.mappingField && opt.mappingField in query) {
			const item = opt.options && opt.options.find(x => x.id === query[opt.mappingField])
			if (item) {
				return {
					...opt,
					value: [item.id],
					filterMode: { [opt.id]: 'ONE' },
					nameList: [getName(item)]
				}
			}
		}
		const level = [FieldType.SINGLE, FieldType.LABEL, FieldType.CHECKBOX].includes(opt.fieldType || '')
			? opt && opt.options && opt.options[0] && opt.options[0].options && opt.options[0].options.length
				? 3
				: 2
			: -1

		let itemOptions = []
		if (level === 3) {
			opt.options &&
				opt.options.forEach(x => {
					let i = x.options && x.options.filter(y => query.keywords && query.keywords.includes(Number(y.id)))
					if (i && i.length) {
						itemOptions = itemOptions.concat(i)
					}
				})
		} else {
			itemOptions = opt.options && opt.options.filter(x => query.keywords && query.keywords.includes(Number(x.id)))
		}
		if (itemOptions && itemOptions.length) {
			return {
				...opt,
				value: itemOptions.map(x => x.id),
				filterMode: opt.options.reduce((acc, x) => {
					acc[x.id] = 'ONE'
					return acc
				}, {}),
				nameList: itemOptions.map(x => getName(x))
			}
		}
	} else if (opt.fieldType === FieldType.SECTION) {
		const itemValue = { min: '', max: '' }
		if (`min_${opt.mappingField || ''}` in query) {
			itemValue.min = query[`min_${opt.mappingField || ''}`]
		}
		if (`max_${opt.mappingField || ''}` in query) {
			itemValue.max = query[`max_${opt.mappingField || ''}`]
		}
		return {
			...opt,
			value: itemValue
		}
	}
	return {}
}

/**
 * 恢复过滤器状态
 * @param {Object} params 参数对象
 * @param {number} params.gameId 游戏ID
 * @param {Object} params.sort 排序响应式数据对象
 * @param {Object} params.order 排序方向响应式数据对象
 * @param {Object} params.sortName 排序名称响应式数据对象
 * @param {Object} params.filter 过滤器对象
 * @param {Object} params.filterSearchPropertyIds 过滤搜索属性ID对象
 */
export function recover(that) {
    const { gameId } = that.$route.params
	const latest = that.$store.getters['filter/getLatest'](gameId)
	if (latest) {
		if (latest.list && latest.list.length) {
			// Vue 2中直接修改响应式数据
			that.filter = {
				...that.filter,
				...getSelectDataByFilterOptions({
					filterOptions: latest.list,
					gameId
				})
			}
            console.log('filter', that.filter)
		}
		if (latest.sort) {
			// Vue 2中直接赋值给响应式数据
			that.sort = latest.sort.sort || ''
			that.order = latest.sort.order || ''
			that.sortName = latest.sort.sortName || ''
		}
		if (latest.quickField && latest.quickField.length) {
			latest.quickField.forEach(x => {
				that.filter[x.searchField] = true
			})
		}
		if (latest.filterSearchPropertyIds && latest.filterSearchPropertyIds.length) {
			latest.filterSearchPropertyIds.forEach(x => {
				if (x && x.id) {
					that.filterSearchPropertyIds[x.id] = [x.id]
				}
			})
		}
		if (latest.server && latest.server.length) {
			latest.server.forEach(x => {
				that.filter[x.mappingField] = x.id
			})
		}
	}
}
/**
 * 恢复外部过滤器数据
 * @param {Object} params 参数对象
 * @param {Object} params.filterData 过滤器数据
 * @param {Object} params.sort 排序响应式数据对象
 * @param {Object} params.order 排序方向响应式数据对象
 * @param {Object} params.sortName 排序名称响应式数据对象
 * @param {Object} params.searchList 搜索列表响应式数据对象
 * @param {Object} params.filter 过滤器对象
 * @param {Object} params.filterSearchPropertyIds 过滤搜索属性ID响应式数据对象
 * @param {Object} params.serverTree 服务器树响应式数据对象
 */
export function recoverExternal({ filterData, that }) {
    const { serverTree, searchList } = that
	// Vue 2中直接赋值给响应式数据
    that.sort = (filterData.sort && filterData.sort.sort) || ''
    that.order = (filterData.sort && filterData.sort.order) || 'ASC'


	if (searchList && searchList.length) {
		searchList.forEach(x => {
			if (x.propertyType === 'FIELD') {
				const v = filterData.quickField && filterData.quickField.find(y => y.searchField === x.searchField)
				that.filter[x.searchField] = v ? true : null
			}
		})
	}

	if (filterData.filterSearchPropertyIds && filterData.filterSearchPropertyIds.length) {
		Object.assign(
			that.filterSearchPropertyIds,
			filterData.filterSearchPropertyIds.reduce((acc, x) => {
				if (x && x.id) {
					acc[x.id] = [x.id]
				}
				return acc
			}, {})
		)
	} else {
		// 清空对象
		Object.keys(that.filterSearchPropertyIds).forEach(key => {
			delete that.filterSearchPropertyIds[key]
		})
	}

	if (serverTree && serverTree.length) {
		let serverTreeFieldIds = serverTree.map(x => x.mappingField)
		serverTreeFieldIds = [...new Set(serverTreeFieldIds)]
		serverTreeFieldIds.forEach(x => {
			const serverItem = filterData.server && filterData.server.find(y => y.mappingField === x)
			that.filter[x] = (serverItem && serverItem.id) || ''
		})
	}
}
